const EVENT_LABEL = "Event";
const MERCH_LABEL = "Merch";
// const SERVICE_TALK_VIDEO_LABEL = "Talk (Video)";
const SONG_LABEL = "Unreleased Song";

export const ACCESS_TYPE_OPTIONS = [
  "post",
  "music",
  "video",
  "merch",
  "experience",
  "event",
  "chat",
] as const;
export const VISIBILITY_OPTIONS = ["public", "fans_only"] as const;
export const MUSIC_TYPE_OPTIONS = ["unreleased", "pre-release"] as const;
export const EXT_SOURCE_OPTIONS = [
  "bandcamp",
  "bandcamp-ingested",
  "ern",
] as const;
export const EVENT_KIND_OPTIONS = [
  "radio_episode",
  "concert",
  "festival",
  "online",
] as const;

export type AccessTypeOptions = (typeof ACCESS_TYPE_OPTIONS)[number];
export type VisibilityOptions = (typeof VISIBILITY_OPTIONS)[number];
export type MusicTypeOptions = (typeof MUSIC_TYPE_OPTIONS)[number];
export type ExtSourceOptions = (typeof EXT_SOURCE_OPTIONS)[number];
export type EventKind = (typeof EVENT_KIND_OPTIONS)[number];

export type AccessPresentation =
  | { key: "concert"; label: "Concert"; tone: "concert" }
  | { key: "online"; label: "Online"; tone: "online" }
  | { key: "song"; label: "Song"; tone: "music" }
  | { key: "dj_set"; label: "DJ Set"; tone: "music" }
  | { key: "merch"; label: "Merch"; tone: "merch" };

const isRinseEpisodeUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (
      (url.hostname === "rinse.fm" || url.hostname === "www.rinse.fm") &&
      url.pathname.startsWith("/episodes/")
    );
  } catch {
    return false;
  }
};

/**
 * Resolve the user-facing subtype for an access without leaking source-specific
 * knowledge into renderers. `radio_episode` and SoundCharts `online` events
 * share the Online presentation while remaining ordinary events everywhere
 * else in the product.
 */
export const getAccessPresentation = (input: {
  type: AccessTypeOptions;
  eventKind?: EventKind | null;
  externalLinks?: readonly string[] | null;
  isSetList?: boolean;
}): AccessPresentation | null => {
  if (input.type === "event") {
    const isOnline =
      input.eventKind === "radio_episode" ||
      input.eventKind === "online" ||
      input.externalLinks?.some(isRinseEpisodeUrl);

    return isOnline
      ? { key: "online", label: "Online", tone: "online" }
      : { key: "concert", label: "Concert", tone: "concert" };
  }

  if (input.type === "music") {
    return input.isSetList
      ? { key: "dj_set", label: "DJ Set", tone: "music" }
      : { key: "song", label: "Song", tone: "music" };
  }

  if (input.type === "merch") {
    return { key: "merch", label: MERCH_LABEL, tone: "merch" };
  }

  return null;
};

export const getAccessTypeDisplayValue = (accessType: AccessTypeOptions) => {
  const LABELS = {
    event: EVENT_LABEL,
    merch: MERCH_LABEL,
    music: SONG_LABEL,
    post: "",
    video: "",
    experience: "",
    chat: "",
  } satisfies Record<AccessTypeOptions, string>;

  return LABELS[accessType];
};
